import { ICONS } from 'components/Icon';
import equal from 'fast-deep-equal';
import { createSelectorCreator, weakMapMemoize } from 'reselect';

export const createSelectorCached = createSelectorCreator({
  argsMemoize: weakMapMemoize,
  memoize: weakMapMemoize,
});

export const createSelectorCachedResultDeepEqual = createSelectorCreator({
  argsMemoize: weakMapMemoize,
  memoize: weakMapMemoize,
  memoizeOptions: {
    resultEqualityCheck: equal,
  },
});

export const SUPPORT_EMAIL = 'support@riskbase.uk';
export const SUPPORT_WHATSAPP = '07700 168 194';
export const SUPPORT_WHATSAPP_URL =
  'https://wa.me/447700168194?text=Hi%20RiskBase,%20%20';
export const SUPPORT_WEBSITE = 'https://riskbase.uk/support';
export const API_DOCS_WEBSITE = 'https://docs-api3.riskbase.uk';
export const SCIM_API_DOCS_WEBSITE = 'https://docs-api3.riskbase.uk/scim';
export const EMPTY_ARRAY: [] = [];

export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export enum ImageGridSizeEnums {
  's' = 's',
  'm' = 'm',
  'l' = 'l',
  'xl' = 'xl',
}

export const FIELD_ICON_SIZE = 21;

export const GENERIC_IMAGE_GRID_SIZE_FILE_MANAGER =
  'GENERIC_IMAGE_GRID_SIZE_FILE_MANAGER';
export const GENERIC_IMAGE_GRID_FIT_FILE_MANAGER =
  'GENERIC_IMAGE_GRID_FIT_FILE_MANAGER';
export const GENERIC_ASSESSMENT_EXPLORER_OPTIONS =
  'GENERIC_ASSESSMENT_EXPLORER_OPTIONS';
export const GenericKeyAseShowSuggestionsUnderChecklistItems =
  'aseShowSuggestionsUnderChecklistItems';

export function eventKeyAseScrollToItemInstanceExplorer({
  assessmentId,
  groupId,
}: {
  assessmentId: string;
  groupId: string;
}) {
  return `ekAseScrollToItemInstanceExplorer:${assessmentId}:${groupId}`;
}

export const genericReportingVisualsSize = 'genericReportingVisualsSize';
export enum ReportingVisualsSizeEnum {
  'small' = 'small',
  'large' = 'large',
  'none' = 'none',
}

export type DeepPartial<T> = T;

export type UUID = string;
export type UID = string;

export enum FilesContextEnum {
  localAsset = 'localAsset',
  remoteAssessment = 'remoteAssessment',
  localAssessment = 'localAssessment',
}

export type FilesContext =
  | {
      context: FilesContextEnum.localAsset;
      assetId: string;
      assessmentId?: undefined;
    }
  | {
      context:
        | FilesContextEnum.remoteAssessment
        | FilesContextEnum.localAssessment;
      assetId: string;
      assessmentId: string;
    };

export enum AppContextEnum {
  localAsset = 'localAsset',
  localAssessment = 'localAssessment',
  remoteAssessment = 'remoteAssessment',
}

export type AppContext =
  | {
      context: AppContextEnum.localAsset;
      assetId: string;
      assessmentId?: undefined;
      isModal?: boolean;
    }
  | {
      context: AppContextEnum.localAssessment | AppContextEnum.remoteAssessment;
      assetId: string;
      assessmentId: string;
      isModal?: boolean;
    };

export const appContextToFileContext = (context: AppContext): FilesContext =>
  context.context === AppContextEnum.remoteAssessment ||
  context.context === AppContextEnum.localAssessment
    ? {
        context:
          context.context === AppContextEnum.localAssessment
            ? FilesContextEnum.localAssessment
            : FilesContextEnum.remoteAssessment,
        assetId: context.assetId,
        assessmentId: context.assessmentId,
      }
    : {
        context: FilesContextEnum.localAsset,
        assetId: context.assetId,
      };
export enum FieldTypes {
  TEXT = 'Text',
  NUMBER = 'Number',
  TIME = 'Time',
  DATE = 'Date',
  OPTION = 'Option',
  IMAGE = 'Image',
  FILE = 'File',
  NOTE = 'Note',
  PIN = 'Pin',
  QR = 'QR',
  GROUP = 'Group',
  GROUPLIGHT = 'GroupLight',
  // NONE,
}

export enum FieldTypeKey {
  text = 'text',
  note = 'note',
  number = 'number',
  checkbox = 'checkbox',
  options = 'options',
  date = 'date',
  time = 'time',
  file = 'file',
  image = 'image',
  pin = 'pin',
  qr = 'qr',
  group = 'group',
  grouplight = 'grouplight',
  divider = 'divider',
}

export enum ImageGridFitEnums {
  'square' = 'square',
  'aspect' = 'aspect',
}

export const ImageGridSizes: Record<
  ImageGridSizeEnums,
  { name: string; key: ImageGridSizeEnums; idealWidth: number }
> = {
  [ImageGridSizeEnums.s]: {
    name: 'Small',
    key: ImageGridSizeEnums.s,
    idealWidth: 100,
  },
  [ImageGridSizeEnums.m]: {
    name: 'Medium',
    key: ImageGridSizeEnums.m,
    idealWidth: 150,
  },
  [ImageGridSizeEnums.l]: {
    name: 'Large',
    key: ImageGridSizeEnums.l,
    idealWidth: 200,
  },
  [ImageGridSizeEnums.xl]: {
    name: 'Extra Large',
    key: ImageGridSizeEnums.xl,
    idealWidth: 300,
  },
};

export enum AssessmentQuestionType {
  question = 'question',
  checklist = 'checklist',
}

export enum TimescalePeriod {
  'day' = 'day',
  'week' = 'week',
  'month' = 'month',
  'year' = 'year',
}

export type TimescaleArray = [
  number,
  (
    | TimescalePeriod.day
    | TimescalePeriod.week
    | TimescalePeriod.month
    | TimescalePeriod.year
  ),
];

/**
 * @deprecated
 */
export enum DeprecatedDueDeterminedBy {
  'type' = 'type',
  'priority' = 'priority',
  'timescale' = 'timescale',
}

export enum DueDeterminedBy {
  'type' = 'type',
  'timescaleBased' = 'timescaleBased',
  'timescale' = 'timescale',
}

/**
 * @deprecated
 */
export type DeprecatedActionDue =
  | {
      determinedBy: DeprecatedDueDeterminedBy.type;
      typeId: number;
      timescale: null;
    }
  | {
      determinedBy: DeprecatedDueDeterminedBy.timescale;
      typeId: null;
      timescale: TimescaleArray;
    }
  | {
      determinedBy: DeprecatedDueDeterminedBy.priority;
      typeId: null;
      timescale: null;
    };

export type FieldResult = {
  fieldId: string;
  value: string | string[];
  pin: { planId: string; x: number; y: number } | '';
  ts: number;
  // delete: delete override when in an assessment
  action: null | 'delete';
};

enum FieldInstanceCheckScheduleStatusEnum {
  'waiting' = 'waiting',
  'completed' = 'completed',
  'attempted' = 'attempted',
  'cancelled' = 'cancelled',
  'missed' = 'missed',
}

export type FieldInstance = {
  instanceId: string;
  groupId: string;
  createdTs: number;
  updatedTs: number;
  reference?: string;
  draft?: boolean;
  /**
   * To delete a field result, set results[string].value=null
   */
  results: Record<string, FieldResult>;
  /**
   * Result autoId 0 if draft in assessment
   */
  autoId: number;
  /**
   * Used within assessments whilst a repeatable is draft. If this equals 2
   * and the max approved autoId is 3, the draft repeatable will get an autoId of 3+2=5
   */
  draftAutoId?: number;
  // Delete to delete instance, remove to remove instance from assessment
  action: null | 'delete' | 'remove';
  // @added 6.28 (2023-06-13) could not upgrade local data
  checks?: Record<
    string,
    {
      id: string;
      lastCompleted: null | { date: number };
      lastAttempted: null | { date: number };
      overdue: boolean;
      schedule: {
        due: { from: number; to: number };
        statusId:
          | FieldInstanceCheckScheduleStatusEnum.attempted
          | FieldInstanceCheckScheduleStatusEnum.completed
          | null;
      };
    }
  >;
};

export type FieldInstanceWithFormattedAutoId = Omit<
  FieldInstance,
  'results'
> & {
  results: Record<string, FieldResult>;
  formattedAutoId?: string;
  inAssessment: boolean;
};

export enum FieldRepeatableOrderEnum {
  'default' = 'default',
  'createdAsc' = 'createdAsc',
  'createdDesc' = 'createdDesc',
  'updatedAsc' = 'updatedAsc',
  'updatedDesc' = 'updatedDesc',
}

export const FieldRepeatableOrderConfig: Record<
  FieldRepeatableOrderEnum,
  {
    type: 'meta' | 'other';
    key: 'createdTs' | 'updatedTs';
    direction: 'asc' | 'desc';
  }
> = {
  [FieldRepeatableOrderEnum.default]: {
    type: 'meta',
    key: 'createdTs',
    direction: 'asc',
  },
  [FieldRepeatableOrderEnum.createdAsc]: {
    type: 'meta',
    key: 'createdTs',
    direction: 'asc',
  },
  [FieldRepeatableOrderEnum.createdDesc]: {
    type: 'meta',
    key: 'createdTs',
    direction: 'desc',
  },
  [FieldRepeatableOrderEnum.updatedAsc]: {
    type: 'meta',
    key: 'updatedTs',
    direction: 'asc',
  },
  [FieldRepeatableOrderEnum.updatedDesc]: {
    type: 'meta',
    key: 'updatedTs',
    direction: 'desc',
  },
};

export enum AssessmentStatusesEnum {
  'inprogress' = 'inprogress',
  'submitted' = 'submitted',
  'approved' = 'approved',
}

export const AssessmentStatusesOrder: AssessmentStatusesEnum[] = [
  AssessmentStatusesEnum.inprogress,
  AssessmentStatusesEnum.submitted,
  AssessmentStatusesEnum.approved,
];

export type AssessmentStatus = {
  id: AssessmentStatusesEnum;
  name: string;
  color: string;
};

export type Account = {
  id: string;
  name: string;
  email: string;
};

export type AssessmentStartDateType = 'assessed' | 'custom';

export type AssessmentExpiryDateType =
  | 'asset'
  | 'assessment_template'
  | 'custom'
  | 'timescale';

export type SelectOption = {
  groupName?: string;
  internalValue: number | string | undefined;
  name: string;
  value?: string;
  labelAlignTop?: boolean;
  icon?: ICONS;
  iconColor?: string;
};

export type OrganisationBasic = {
  id: UID;
  name: string;
};

export enum MonthEnum {
  January = 'January',
  February = 'February',
  March = 'March',
  April = 'April',
  May = 'May',
  June = 'June',
  July = 'July',
  August = 'August',
  September = 'September',
  October = 'October',
  November = 'November',
  December = 'December',
}

export const Months: { name: MonthEnum; nameShort: string; number: number }[] =
  [
    {
      name: MonthEnum.January,
      nameShort: 'Jan',
      number: 1,
    },
    {
      name: MonthEnum.February,
      nameShort: 'Feb',
      number: 2,
    },
    {
      name: MonthEnum.March,
      nameShort: 'Mar',
      number: 3,
    },
    {
      name: MonthEnum.April,
      nameShort: 'Apr',
      number: 4,
    },
    {
      name: MonthEnum.May,
      nameShort: 'May',
      number: 5,
    },
    {
      name: MonthEnum.June,
      nameShort: 'Jun',
      number: 6,
    },
    {
      name: MonthEnum.July,
      nameShort: 'Jul',
      number: 7,
    },
    {
      name: MonthEnum.August,
      nameShort: 'Aug',
      number: 8,
    },
    {
      name: MonthEnum.September,
      nameShort: 'Sep',
      number: 9,
    },
    {
      name: MonthEnum.October,
      nameShort: 'Oct',
      number: 10,
    },
    {
      name: MonthEnum.November,
      nameShort: 'Nov',
      number: 11,
    },
    {
      name: MonthEnum.December,
      nameShort: 'Dec',
      number: 12,
    },
  ];

export type ISODateString = string & { __brand: 'ISODate' };
